import React, { useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Medal, TrendingUp, Zap, ChevronDown, ChevronUp, RotateCcw, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Map } from "lucide-react";

const COG_LABELS = {
  analytic: "Analítico",
  creative: "Criativo",
  strategic: "Estratégico",
  executor: "Executor",
};

const RISK_LABELS = {
  high: "Alta tolerância ao risco",
  medium: "Tolerância moderada",
  low: "Baixa tolerância ao risco",
};

const BAGGAGE_LABELS = {
  tech: "Tecnologia & Exatas",
  humanities: "Humanas & Comunicação",
  sales: "Vendas & Comercial",
  design: "Design & Artes",
  writing: "Escrita & Conteúdo",
  management: "Gestão & Processos",
  analysis: "Análise & Dados",
  visual: "Criação Visual",
};

const PROFESSION_INSIGHTS = {
  // ── Conteúdo & Criação ──────────────────────────────────────────────────────
  "Copywriter":               { why: ["Alto perfil criativo com foco argumentativo", "Busca por autonomia e ritmo próprio", "Capacidade de transformar ideias em persuasão"], risks: ["Perfeccionismo pode travar entregas", "Tendência à procrastinação sem prazo externo"], action: "Escreva 3 copies hoje — anúncio, e-mail e página de vendas — e publique ou compartilhe para feedback." },
  "Redator":                  { why: ["Perfil criativo orientado à escrita profunda", "Valoriza autonomia e impacto via conteúdo", "Habilidade de comunicação altamente transferível"], risks: ["Mercado aberto dificulta precificação justa", "Dependência de briefings mal definidos de clientes"], action: "Escolha um nicho (saúde, tech, finanças) e escreva um artigo de 600 palavras otimizado para SEO." },
  "Estrategista de Conteúdo": { why: ["Pensamento estratégico aliado à criação", "Combina análise de dados com planejamento editorial", "Autonomia criativa como motor de qualidade"], risks: ["Pode se perder no planejamento sem executar", "Dificuldade de mostrar ROI de conteúdo de médio prazo"], action: "Monte um calendário editorial de 30 dias para uma marca real ou fictícia e justifique cada decisão." },
  "Social Media":             { why: ["Alta criatividade com visão de dados e tendências", "Perfil comunicador com energia para adaptação constante", "Fit natural com o ritmo acelerado das redes"], risks: ["Burnout por exposição e disponibilidade constante", "Dificuldade real de desconectar fora do horário"], action: "Escolha uma rede social e publique um conteúdo hoje — mesmo que imperfeito. Velocidade > perfeição." },

  // ── Design & Vídeo ──────────────────────────────────────────────────────────
  "Designer":                 { why: ["Perfil altamente criativo com visão estética apurada", "Autonomia criativa como motor de qualidade", "Resultado visual concreto e portfólio tangível"], risks: ["Revisões infinitas por briefings vagos de clientes", "Tendência a misturar ego com o trabalho"], action: "Pegue uma marca que você admira e recrie sua identidade visual com um estilo diferente. Poste o before/after." },
  "Web Designer":             { why: ["Une criatividade visual com raciocínio técnico-lógico", "Resultado direto mensurável (site que converte)", "Alta demanda no mercado digital com poucos bons profissionais"], risks: ["Escopo mal definido gera retrabalho e desgaste", "Precificação errada na fase inicial é muito comum"], action: "Construa uma landing page simples usando Webflow ou Framer — mesmo sem cliente real. Publique e peça feedback." },
  "Videomaker":               { why: ["Criatividade aliada ao senso de narrativa audiovisual", "Autonomia total sobre o resultado final", "Alta valorização no mercado de conteúdo e produtoras digitais"], risks: ["Equipamento inicial pode ser barreira percebida (mas não real)", "Clientes costumam subestimar o tempo de produção"], action: "Grave e edite um vídeo de 60 segundos explicando algo que você domina. Distribua para 5 pessoas e peça feedback honesto." },
  "Editor de Vídeos":         { why: ["Perfil executor com atenção a detalhes e ritmo", "Rotina previsível com entregas bem definidas", "Alta demanda com crescimento acelerado de criadores de conteúdo"], risks: ["Ritmo de bastidor pode ser solitário e isolante", "Revisões sem fim sem contrato bem estruturado"], action: "Edite um vídeo bruto (seu ou de alguém) e compare before/after. Compartilhe o resultado e peça avaliação." },

  // ── Analytics & Performance ─────────────────────────────────────────────────
  "Analista de Marketing":       { why: ["Perfil analítico forte com visão de negócio digital", "Dados como base para decisão — reduz dependência de feeling", "Crescimento de carreira estruturado em médias e grandes empresas"], risks: ["Paralisia por análise — dificuldade de partir para a ação", "Comunicar dados para áreas não técnicas exige tradução constante"], action: "Baixe uma planilha de métricas de marketing (ou crie uma fictícia) e apresente em 5 slides os 3 maiores problemas e soluções." },
  "Analista de E-mail Marketing":{ why: ["Canal de alto ROI e baixa concorrência de especialistas", "Perfil técnico-criativo equilibrado com resultado direto", "Métricas claras: taxa de abertura, clique, conversão, receita"], risks: ["Área considerada 'pouco glamourosa' no mercado de conteúdo", "Domínio de ferramentas específicas (Klaviyo, RD Station) é pré-requisito"], action: "Escreva uma sequência de 3 e-mails de boas-vindas para uma empresa fictícia. Capriche no assunto de cada um." },
  "Gestor de Tráfego":           { why: ["Perfil analítico com tolerância a variáveis e risco calculado", "Impacto direto e mensurável atrelado ao investimento", "Uma das áreas com maior demanda e melhor remuneração no digital"], risks: ["Queimar verba de cliente na fase de testes é inerente e estressante", "Pressão constante por ROI positivo exige resiliência emocional"], action: "Crie uma campanha fictícia no Meta Ads Manager (modo rascunho) e simule segmentação de público para um produto real." },
  "Estrategista Digital":        { why: ["Visão macro de negócio aliada à execução digital", "Perfil raro que combina dados, criatividade e estratégia", "Alta valorização em agências, consultorias e marcas com ambição de escala"], risks: ["Difícil mostrar o valor intangível da estratégia antes dos resultados", "Tendência a ser generalista sem especialização — escolha um setor"], action: "Escolha uma marca e monte um diagnóstico de presença digital em 1 página: onde está, onde deveria estar, como chegar lá." },
  "Analista de Infraestrutura de Marketing Digital": { why: ["Perfil técnico especializado com altíssima demanda e baixa oferta", "Trabalho estruturado de bastidor sem exposição desnecessária", "Segurança e previsibilidade profissional elevada"], risks: ["Pode se tornar gargalo operacional em times pequenos sem documentação", "Evolução de carreira depende de atualização técnica constante"], action: "Configure um funil básico no RD Station ou HubSpot gratuito com automação de 3 etapas e teste o fluxo completo." },

  // ── Vendas & Comercial ──────────────────────────────────────────────────────
  "Gestor Comercial":    { why: ["Alta ambição financeira com perfil de liderança natural", "Conforto real com risco, pressão e metas agressivas", "Capacidade de montar e escalar times comerciais"], risks: ["Impaciência com processos lentos pode gerar atritos", "Tendência a pular etapas quando o resultado tarda"], action: "Estude o funil comercial de uma empresa que você admira e mapeie onde eles ganham e onde perdem clientes." },
  "Closer":             { why: ["Alta energia em fechamento com comunicação extremamente fluida", "Tolerância ao risco elevada — rejeição não te paralisa", "Ganho financeiro variável e ilimitado como motivador"], risks: ["Instabilidade emocional em meses de baixa performance", "Dificuldade com rotina e processos rígidos"], action: "Pratique uma abordagem de fechamento hoje — escolha um produto e simule o pitch completo com alguém de confiança." },
  "SDR":                { why: ["Perfil comunicador com energia e foco em resultado rápido", "Porta de entrada sólida para carreira comercial com progressão clara", "Habilidade de prospecção transferível para qualquer mercado ou produto"], risks: ["Volume de rejeições diárias pode desgastar emocionalmente sem gestão", "Salário fixo baixo exige disciplina no variável"], action: "Pesquise 10 empresas do seu nicho ideal e escreva uma cadência de prospecção de 5 contatos — canal, mensagem e timing." },
  "BDR":                { why: ["Combina prospecção estratégica com visão de expansão de mercado", "Posição valorizada que gera negócios novos em territórios inexplorados", "Ticket médio mais alto com mais influência no processo de venda"], risks: ["Ciclo de venda mais longo exige paciência e fôlego financeiro", "Pressão por geração de pipeline em mercados ainda sem prova"], action: "Mapeie 3 segmentos de mercado não explorados por uma empresa (real ou fictícia) e justifique por que são oportunidades." },
  "Vendedor de WhatsApp":{ why: ["Alta energia comercial com canal de comunicação direto e pessoal", "Resultado imediato e visível — venda acontece na hora", "Habilidade totalmente transferível para qualquer produto digital"], risks: ["Burnout por volume de mensagens e ausência de limites de horário", "Dificuldade de desconectar sem processos claros de atendimento"], action: "Crie um script completo de venda para um produto que você usaria: abertura, qualificação, oferta e fechamento." },
  "Social Seller":      { why: ["Combina redes sociais com processo comercial — habilidade do futuro", "Alta autonomia e criatividade dentro do processo de vendas", "Fit natural para quem já cria conteúdo e quer monetizá-lo"], risks: ["Linha tênue entre conteúdo de valor e pitch agressivo", "Construção de audiência leva tempo antes de gerar venda consistente"], action: "Poste hoje um conteúdo que educa e, no final, convida de forma natural para uma conversa sobre o produto." },
  "Sales Ops":          { why: ["Perfil técnico-analítico que sustenta e escala times comerciais", "Alta valorização em scale-ups e empresas de médio porte em crescimento", "Combina dados, processos e tecnologia com visão de negócio"], risks: ["Área pouco visível internamente até algo quebrar", "Dependência de ferramentas como Salesforce, HubSpot, Pipedrive exige atualização constante"], action: "Mapeie o funil comercial de uma empresa e identifique onde estão os 3 maiores gargalos com dados." },

  // ── Produto & Operações ─────────────────────────────────────────────────────
  "Gestor de Produto Digital":    { why: ["Visão estratégica + analítica com foco em impacto real", "Alta ambição e senso de dono — trata o produto como negócio", "Conforto com autonomia e ambiguidade como motor criativo"], risks: ["Pode frustrar times por exigir perfeição sem prazo", "Dificuldade de priorizar quando tudo parece urgente"], action: "Mapeie o fluxo de um produto digital que você usa todo dia e identifique 3 oportunidades claras de melhoria." },
  "Gestor de Operações Digitais": { why: ["Perfil estratégico-operacional raro e muito demandado", "Garante que times digitais entreguem com consistência e qualidade", "Alta visão sistêmica de negócio — conecta todas as áreas"], risks: ["Pode se perder em múltiplos fronts simultâneos sem priorização", "Dificuldade de mensurar impacto direto em receita no curto prazo"], action: "Mapeie o fluxo operacional de um time digital em um fluxograma ou BPMN simples. Identifique onde há desperdício." },
  "Gerente de Projetos Digitais": { why: ["Visão estruturada que faz times entregarem com qualidade e prazo", "Alta demanda em agências, scale-ups e consultorias digitais", "Habilidade transferível para qualquer setor que dependa de execução"], risks: ["Responsabilidade sem autoridade é o maior estressor da função", "Prazo e escopo mal definidos geram retrabalho e desgaste constante"], action: "Monte um cronograma de projeto fictício no Notion ou Trello com milestones, responsáveis e critérios de sucesso." },

  // ── CRM & Customer ──────────────────────────────────────────────────────────
  "Analista de CRM":                 { why: ["Perfil analítico forte com foco em processos e rotina estruturada", "Resultado mensurável com impacto direto na retenção de clientes", "Baixo risco e alta segurança profissional em médias e grandes empresas"], risks: ["Excesso de análise pode atrasar implementações", "Comunicar valor das ações de CRM para gestores não técnicos exige esforço"], action: "Explore a interface de um CRM gratuito (HubSpot, Bitrix) e construa um pipeline de 5 etapas com automação básica." },
  "Analista de Customer Success":    { why: ["Alto impacto direto na receita via retenção e expansão de contas", "Combina análise de dados, relacionamento humano e visão estratégica", "Área em rápido crescimento com boa remuneração em empresas SaaS"], risks: ["Churn de clientes pode frustrar mesmo com trabalho excelente", "Limites entre CS, Suporte e Produto frequentemente geram conflito de escopo"], action: "Crie um playbook de onboarding de 30 dias para um cliente fictício de SaaS — milestones, check-ins e critérios de sucesso." },
  "Analista de Customer Experience": { why: ["Visão holística da jornada do cliente — habilidade cada vez mais estratégica", "Combina análise quantitativa com empatia real pelo usuário", "Alta valorização em empresas que investem em crescimento via retenção"], risks: ["Mudanças dependem de múltiplas áreas — influência sem autoridade formal", "Métricas de CX (NPS, CSAT) difíceis de vender para líderes orientados a receita"], action: "Mapeie a jornada completa de um cliente de uma empresa que você usa. Identifique 3 pontos de atrito e proponha soluções." },
  "Gestor de Atendimento Digital":   { why: ["Combina habilidade humana com processos escaláveis e tecnologia", "Alta demanda em empresas de e-commerce, SaaS e educação digital", "Impacto direto e mensurável na satisfação e retenção de clientes"], risks: ["Lidar com clientes insatisfeitos diariamente exige resiliência emocional", "Escalar atendimento sem perder qualidade é o maior desafio da função"], action: "Escreva 5 respostas modelo para reclamações comuns de um e-commerce com tom empático, claro e orientado à solução." },
  "Suporte a Alunos e Usuários":     { why: ["Perfil altamente empático com foco real em resolver problemas", "Porta de entrada sólida em empresas de educação digital e SaaS", "Rotina previsível com alto impacto no sucesso do usuário"], risks: ["Progressão de carreira pode parecer lenta sem iniciativa proativa", "Exposição constante à frustração alheia exige gestão emocional ativa"], action: "Crie uma FAQ de 10 perguntas para um curso online fictício com respostas empáticas, diretas e completas." },

  // ── Comunidade & Tech ───────────────────────────────────────────────────────
  "Gestor de Comunidade":   { why: ["Alta capacidade empática com comunicação autêntica e direta", "Área em aceleração com comunidades pagas como modelo de negócio", "Impacto direto e mensurável no engajamento e retenção de membros"], risks: ["Burnout por estar sempre 'online' e disponível para a comunidade", "Dificuldade de escalar engajamento sem perder o toque humano"], action: "Crie um plano de onboarding de 7 dias para membros novos de uma comunidade fictícia — boas-vindas, ativações e primeiro valor." },
  "Desenvolvedor Vibe Coder": { why: ["Forte perfil técnico com criatividade aplicada à construção de produtos", "Autonomia total no processo — de ideia a produto publicado", "Resultado visível, tangível e fácil de mostrar em portfólio"], risks: ["Isolamento por trabalho predominantemente solo", "Dificuldade de comunicar o valor do produto para não-técnicos"], action: "Construa algo simples com IA hoje — uma landing page, um script, um mini-app. Publique e compartilhe o link." },

  // ── Gestão de Marketing ──────────────────────────────────────────────────────
  "Gestor de Marketing":    { why: ["Visão estratégica 360° que conecta todas as disciplinas de marketing", "Perfil de liderança com ambição de crescimento e escala", "Alta capacidade de orquestrar times criativos e analíticos ao mesmo tempo"], risks: ["Dificuldade de largar a execução e confiar plenamente no time", "Pressão por resultado imediato pode sacrificar estratégias de médio prazo"], action: "Mapeie todas as frentes de marketing de uma empresa real ou fictícia, defina 3 prioridades do trimestre e justifique o critério de escolha." },
};

function getInsights(name) {
  return PROFESSION_INSIGHTS[name] || {
    why: ["Perfil compatível com as demandas dessa área", "Combina suas habilidades com as exigências do mercado", "Bom alinhamento entre seu estilo de trabalho e a função"],
    risks: ["Verifique as exigências técnicas da área antes de avançar", "Busque referências de quem já atua nessa posição"],
    action: "Pesquise profissionais que atuam nessa área, conecte-se com eles e peça 30 minutos de conversa para entender o dia a dia da função.",
  };
}

function getRankIcon(rank) {
  if (rank === 0) return <Trophy className="w-4 h-4" style={{ color: "#FFD700" }} />;
  if (rank === 1) return <Medal className="w-4 h-4" style={{ color: "#C0C0C0" }} />;
  if (rank === 2) return <Medal className="w-4 h-4" style={{ color: "#CD7F32" }} />;
  return <span className="text-xs font-bold" style={{ color: "#99A6AB" }}>#{rank + 1}</span>;
}

function getRetakeDaysRemaining(completedAt) {
  if (!completedAt) return 0;
  const completed = new Date(completedAt);
  if (isNaN(completed.getTime())) return 0;
  const unlockDate = new Date(completed);
  unlockDate.setDate(unlockDate.getDate() + 90);
  const now = new Date();
  const diff = Math.ceil((unlockDate - now) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

const RANK_LABELS = ["1ª maior compatibilidade", "2ª maior compatibilidade", "3ª maior compatibilidade"];

export default function VocationalResult({ result, user, saving, completedAt, onRetake }) {
  const [expandedCard, setExpandedCard] = useState(0);
  const [showFullRanking, setShowFullRanking] = useState(false);
  const top3 = result.topProfessions.slice(0, 3);
  const allProfessions = result.topProfessions;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
      className="px-4 py-10"
    >
      <div className="max-w-2xl mx-auto space-y-8">

        {/* Header */}
        <div className="text-center">
          <p
            className="text-xs font-medium uppercase tracking-widest mb-3"
            style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}
          >
            Diagnóstico Vocacional
          </p>
          <h1
            className="text-2xl font-bold mb-2"
            style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}
          >
            Seu diagnóstico está pronto
          </h1>
          <p
            className="text-sm"
            style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F" }}
          >
            {user?.full_name ? `${user.full_name.split(" ")[0]}, estas` : "Estas"} são as carreiras com maior compatibilidade com seu perfil.
          </p>
        </div>

        {/* Top 3 Cards */}
        <div className="space-y-4">
          {top3.map((prof, i) => {
            const insights = getInsights(prof.name);
            const isExpanded = expandedCard === i;
            return (
              <div
                key={prof.name}
                className="rounded-2xl p-5 bg-white transition-all"
                style={{
                  border: "1px solid #E5E9EA",
                  boxShadow: "0 4px 16px -2px rgba(0,0,0,0.06), 0 1px 3px 0 rgba(0,0,0,0.04)",
                }}
              >
                {/* Header row */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: "#EAFAF6" }}>
                    {getRankIcon(i)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium uppercase tracking-wider mb-0.5"
                      style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}>
                      {RANK_LABELS[i]}
                    </p>
                    <h3 className="text-base font-bold truncate"
                      style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
                      {prof.name}
                    </h3>
                  </div>
                  <span className="text-2xl font-bold flex-shrink-0" style={{ color: "#00DCA4" }}>
                    {prof.score}%
                  </span>
                </div>

                {/* Score bar */}
                <div className="w-full h-1.5 rounded-full mb-4" style={{ background: "#F2F4F4" }}>
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: "#00DCA4" }}
                    initial={{ width: 0 }}
                    animate={{ width: `${prof.score}%` }}
                    transition={{ duration: 0.8, delay: 0.2 + i * 0.15 }}
                  />
                </div>

                {/* Expand/collapse toggle */}
                <button
                  onClick={() => setExpandedCard(isExpanded ? -1 : i)}
                  className="flex items-center gap-1.5 text-xs font-medium mb-1 transition-colors"
                  style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F" }}
                >
                  {isExpanded
                    ? <><ChevronUp className="w-3 h-3" /> Fechar detalhes</>
                    : <><ChevronDown className="w-3 h-3" /> Ver detalhes</>
                  }
                </button>

                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    transition={{ duration: 0.25 }}
                    className="mt-3 space-y-4"
                  >
                    {/* Why */}
                    <div>
                      <p className="text-xs font-semibold mb-2 flex items-center gap-1"
                        style={{ fontFamily: "Work Sans, sans-serif", color: "#00664C" }}>
                        <Zap className="w-3 h-3" /> Por que combina com você
                      </p>
                      <ul className="space-y-1">
                        {insights.why.map((w, j) => (
                          <li key={j} className="text-sm flex items-start gap-2"
                            style={{ fontFamily: "Work Sans, sans-serif", color: "#2E3233" }}>
                            <span style={{ color: "#00DCA4" }}>•</span> {w}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* CTA */}
                    <Link to={`${createPageUrl("MarketMap")}?profession=${encodeURIComponent(prof.name)}`} className="block mt-2">
                      <button className="w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all hover:scale-[1.01]"
                        style={{ fontFamily: "Work Sans, sans-serif", border: "1.5px solid #00DCA4", color: "#00664C", background: "#fff" }}>
                        <Map className="w-4 h-4" /> Ver detalhes da profissão
                      </button>
                    </Link>
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>

        {/* Full Ranking */}
        <div
          className="rounded-2xl p-5 bg-white"
          style={{
            border: "1px solid #E5E9EA",
            boxShadow: "0 1px 2px 0 rgba(0,0,0,0.04)",
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4" style={{ color: "#00DCA4" }} />
            <p className="text-sm font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
              Ranking completo — {allProfessions.length} carreiras
            </p>
          </div>

          <div className="space-y-1">
            {(showFullRanking ? allProfessions : allProfessions.slice(0, 10)).map((p, i) => (
              <div key={p.name} className="flex items-center gap-3 py-2">
                <div className="w-6 flex-shrink-0 flex items-center justify-center">
                  {getRankIcon(i)}
                </div>
                <p className="flex-1 text-sm font-medium truncate"
                  style={{ fontFamily: "Work Sans, sans-serif", color: i < 3 ? "#1A1D1E" : "#646C6F" }}>
                  {p.name}
                </p>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="w-20 h-1.5 rounded-full" style={{ background: "#F2F4F4" }}>
                    <div className="h-full rounded-full" style={{
                      background: i < 3 ? "#00DCA4" : "#B0FCEA",
                      width: `${p.score}%`,
                    }} />
                  </div>
                  <span className="text-xs font-bold w-8 text-right"
                    style={{ color: i < 3 ? "#00664C" : "#99A6AB" }}>
                    {p.score}%
                  </span>
                </div>
              </div>
            ))}
          </div>

          {allProfessions.length > 10 && (
            <button onClick={() => setShowFullRanking(v => !v)}
              className="mt-3 text-xs font-semibold flex items-center gap-1 w-full justify-center py-2 rounded-xl transition-all"
              style={{ fontFamily: "Work Sans, sans-serif", color: "#646C6F", background: "#F2F4F4" }}>
              {showFullRanking
                ? <><ChevronUp className="w-3 h-3" /> Ver menos</>
                : <><ChevronDown className="w-3 h-3" /> Ver todas as {allProfessions.length} carreiras</>
              }
            </button>
          )}
        </div>

        {/* Profile Summary */}
        <div
          className="rounded-2xl p-5 bg-white"
          style={{
            border: "1px solid #E5E9EA",
            boxShadow: "0 1px 2px 0 rgba(0,0,0,0.04)",
          }}
        >
          <p className="text-sm font-bold mb-4" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>Seu perfil resumido</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Perfil Cognitivo",    value: COG_LABELS[result.cognitiveProfile]   || result.cognitiveProfile },
              { label: "Tolerância a Risco",  value: RISK_LABELS[result.riskProfile]        || result.riskProfile },
              { label: "Área de Origem",      value: BAGGAGE_LABELS[result.baggageProfile]  || result.baggageProfile },
              { label: "Habilidade Dominante",value: BAGGAGE_LABELS[result.skillProfile]    || result.skillProfile },
            ].map((item, i) => (
              <div key={i} className="rounded-xl p-3 text-center" style={{ background: "#EAFAF6", border: "1px solid #B0FCEA" }}>
                <p className="text-xs mb-1" style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}>{item.label}</p>
                <p className="text-xs font-bold" style={{ fontFamily: "Work Sans, sans-serif", color: "#00664C" }}>{item.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Retake + Saving */}
        <div className="flex flex-col items-center gap-3 pt-4">
          {saving && (
            <p
              className="text-center text-xs"
              style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}
            >
              Salvando resultado...
            </p>
          )}
          {onRetake && (() => {
            const daysRemaining = getRetakeDaysRemaining(completedAt);
            const locked = daysRemaining > 0;
            return (
              <div className="flex flex-col items-center gap-1.5">
                <button
                  onClick={locked ? undefined : onRetake}
                  disabled={locked}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-medium transition-all"
                  style={{
                    fontFamily: "Work Sans, sans-serif",
                    background: locked ? "#F2F4F4" : "#FAFBFB",
                    color: locked ? "#C8CED0" : "#646C6F",
                    border: `1px solid ${locked ? "#E5E9EA" : "#E5E9EA"}`,
                    cursor: locked ? "not-allowed" : "pointer",
                  }}
                >
                  {locked
                    ? <Lock className="w-3.5 h-3.5" />
                    : <RotateCcw className="w-3.5 h-3.5" />
                  }
                  Refazer teste
                </button>
                {locked && (
                  <p
                    className="text-[11px]"
                    style={{ fontFamily: "Work Sans, sans-serif", color: "#99A6AB" }}
                  >
                    disponível em {daysRemaining} {daysRemaining === 1 ? "dia" : "dias"}
                  </p>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </motion.div>
  );
}
